import { useMemo } from 'react';

import { BUSINESS_TOOL_SECTIONS, IBusinessToolSections } from 'constants/businessTool';
import { IForBankSections } from 'constants/businessToolSections/forBank';
import { IForInvestorsSections } from 'constants/businessToolSections/forInvestors';
import { IOnePageSections } from 'constants/businessToolSections/onePage';
import get from 'lodash/get';
import { AnyObject } from 'yup';

import { IBusinessPlanType } from 'interfaces/businessPlan';

import { filterObjByKeys } from 'components/BusinessToolForm/components/SectionsNavigationItem/hooks/utils';
import { forBankSectionsMap } from 'components/BusinessToolForm/utils/filledSectionCalculation/forBankContentPanelData';
import { forInvestorsSectionsMap } from 'components/BusinessToolForm/utils/filledSectionCalculation/forInvestorsPanelData';
import { onePageSectionsMap } from 'components/BusinessToolForm/utils/filledSectionCalculation/onePagePanelData';
import { IBusinessToolForm, useGetInitialValues } from 'components/BusinessToolForm/utils/form';

import { calculateFilledPercentage } from 'helpers/businessTool';

export const useCalculateFilledPercentage = (
  businessPlanType: IBusinessPlanType,
  businessPlanChapters: IBusinessToolForm,
  global: any,
  sectionName: string,
) => {
  const initialValues = useGetInitialValues();
  let chapterName: IBusinessToolSections = 'OVERVIEW';
  let initValues: AnyObject = initialValues;
  let currentValues: AnyObject = businessPlanChapters;
  let fieldsToIgnore: string[] = [];
  let allowedGlobalKeys: string[] = [];
  let fieldsToCheck: string[] = [];
  let additionalFields: string[] = [];

  if (sectionName !== 'TITLE_PAGE_AND_TABLE_OF_CONTENTS') {
    if (businessPlanType === 'LIGHT') {
      chapterName = forInvestorsSectionsMap[sectionName as IForInvestorsSections].chapterName;
      allowedGlobalKeys = forInvestorsSectionsMap[sectionName as IForInvestorsSections].globalFields;
      fieldsToIgnore = forInvestorsSectionsMap[sectionName as IForInvestorsSections].fieldsToIgnore;
      additionalFields = forInvestorsSectionsMap[sectionName as IForInvestorsSections].additionalFieldsToCheck;
      fieldsToCheck = forInvestorsSectionsMap[sectionName as IForInvestorsSections].checkOnlyTheseFields;
    } else if (businessPlanType === 'BANK') {
      chapterName = forBankSectionsMap[sectionName as IForBankSections].chapterName;
      allowedGlobalKeys = forBankSectionsMap[sectionName as IForBankSections].globalFields;
      fieldsToIgnore = forBankSectionsMap[sectionName as IForBankSections].fieldsToIgnore;
      fieldsToCheck = forBankSectionsMap[sectionName as IForBankSections].checkOnlyTheseFields;
    }
  }

  if (businessPlanType === 'LIGHT' || businessPlanType === 'BANK') {
    if (sectionName === 'TITLE_PAGE_AND_TABLE_OF_CONTENTS') {
      const { companyName, coverTitle } = businessPlanChapters.GLOBAL;
      const { file } = businessPlanChapters.OVERVIEW.company;
      initValues = { companyName: '', file: initialValues.OVERVIEW.company.file, coverTitle: '' };
      currentValues = { companyName, file, coverTitle };
    } else {
      const currentSection = businessPlanChapters[chapterName];
      const currentChapterInitialValues = initialValues[chapterName];
      const globalInitialValues = initialValues[BUSINESS_TOOL_SECTIONS.GLOBAL];

      const globalFields = filterObjByKeys(global, allowedGlobalKeys);
      const globalInit = filterObjByKeys(globalInitialValues, allowedGlobalKeys);

      initValues = {
        ...currentChapterInitialValues,
        ...globalInit,
      };
      currentValues = {
        ...currentSection,
        ...globalFields,
      };

      if (additionalFields.length > 0) {
        additionalFields.forEach((field) => {
          const initVal = get(initialValues, field);
          const currVal = get(businessPlanChapters, field);
          initValues = {
            ...initValues,
            [field]: initVal,
          };
          currentValues = {
            ...currentValues,
            [field]: currVal,
          };
        });
      }
    }
  }

  if (businessPlanType === 'ONE_PAGE') {
    fieldsToCheck = onePageSectionsMap[sectionName as IOnePageSections];
  }

  const percent = useMemo(
    () => calculateFilledPercentage(initValues, currentValues, fieldsToCheck, fieldsToIgnore),
    [initValues, currentValues],
  );

  return percent;
};
